import { useState } from "react";

import { useForm } from "@common/hooks/useForm";
import { UserData } from "@common/models";
import { identifyIntercom } from "@common/util/intercom";
import { validateEmail } from "@common/util/ValidatorFunctions";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAuthContext } from "@main/context/Auth/Auth.context";

import { validateUserInfoName } from "../../util/validatorFunctions";
import usePersonalInfoApiConsumer from "../api/usePersonalInfoApiConsumer";

interface AccountBasicsFormState {
  email: string;
  name: string;
}

const validators = {
  email: validateEmail,
  name: validateUserInfoName,
};

enum ErrorStatusCodes {
  EMAIL_ALREADY_IN_USE = 409,
}

export default function usePersonalInfoBasicsFormConsumer() {
  const { userData } = useAuthContext();
  const { selectedWorkspace } = useAppSelectionContext();
  const {
    updateUserInfoBasics,
    handlePictureUpdate,
    loading: pictureUpdating,
    deletePicture,
  } = usePersonalInfoApiConsumer();

  const { handleFormChange, formState, formError, handleFormSubmit, setError } = useForm<AccountBasicsFormState>({
    initialState: { email: userData?.email ?? "", name: userData?.name ?? "" },
    onSubmit: handleUserInfoUpdate,
    validators,
  });

  const [loading, setLoading] = useState(false);

  async function handleUserInfoUpdate() {
    try {
      setLoading(true);

      const oldUserInfo = {
        email: userData?.email,
        name: userData?.name,
      };

      const newUserInfo = {
        email: formState.email,
        name: formState.name,
      };

      await updateUserInfoBasics(newUserInfo, oldUserInfo);

      identifyIntercom(getNewUserData(newUserInfo.email, newUserInfo.name, userData), undefined, selectedWorkspace);
    } catch (error) {
      if (error.response?.status === ErrorStatusCodes.EMAIL_ALREADY_IN_USE) {
        setError({ email: new Error("The provided email is already in use, please use a different one") });
      }
    } finally {
      setLoading(false);
    }
  }

  return {
    handleFormChange,
    handleFormSubmit,
    formState,
    formError,
    loading,
    handlePictureUpdate,
    pictureUpdating,
    deletePicture,
  };
}

const getNewUserData = (newEmail?: string, newName?: string, userData?: UserData) => {
  if (!userData) return { email: newEmail ?? "", name: newName ?? "" };
  return { ...userData, email: newEmail, name: newName };
};
